import { toast } from "sonner";

import { getErrorMessage } from "./error-message";
import { readFieldErrors } from "./field-errors";

/**
 * ONE toast per failed request, and it is the server's own message.
 *
 * THE BUG THIS EXISTS FOR. `axiosInstance`'s response interceptor already
 * toasts every non-suppressed API failure with the reason the server gave
 * ("Insufficient balance. You need at least 1.872 polygon…"). Feature code
 * then toasted again from its own `catch`, with a generic line of its own
 * ("Failed to create collection"). Sonner stacks newest on top, so the
 * generic one covered the useful one and the user had to click a toast away
 * to find out why anything failed — the exact opposite of what each message
 * was for.
 *
 * Deleting the second toast outright would be wrong: some of those catches
 * also fire for errors that never went through the interceptor — a thrown
 * validation error, a rejected wallet popup, a `suppressToast` call. Those
 * still need to say something.
 *
 * So the interceptor MARKS what it has already spoken about, and this decides
 * at runtime: if the user has been told, stay quiet; if not, say the best
 * thing we can resolve. Call sites do not have to know which kind of error
 * they caught, which is what makes this safe to apply everywhere.
 */

/**
 * Set on the error object itself rather than kept in a module-level set: an
 * error is a single object handed to exactly the catch that needs this
 * answer, so there is nothing to expire, nothing to leak, and no chance of a
 * concurrent request's failure being mistaken for this one's.
 */
const TOASTED = Symbol.for("nftbaz.error.toasted");

/** Called by the axios interceptor, immediately after it toasts. */
export function markErrorToasted(error: unknown): void {
  if (error && typeof error === "object") {
    try {
      (error as Record<symbol, unknown>)[TOASTED] = true;
    } catch {
      // A frozen error object. Nothing to record, so `toastApiError` will
      // show its own message — a duplicate is a far smaller problem than a
      // silent failure.
    }
  }
}

/** Has the interceptor already shown this error to the user? */
export function wasErrorToasted(error: unknown): boolean {
  return Boolean(
    error && typeof error === "object" && (error as Record<symbol, unknown>)[TOASTED],
  );
}

/**
 * Report a caught error to the user, unless it has already been reported.
 *
 * Returns whether a toast was actually shown, for the rare caller that needs
 * to know (e.g. one deciding whether to also render an inline message).
 *
 * `message` overrides what would be read off the response — use it where the
 * client can say something better than the raw payload, such as a backend
 * translation KEY the feature knows how to render. Note that an overriding
 * message is only worth passing on a request that also sets
 * `suppressToast: true`; otherwise the interceptor has already shown the raw
 * version and this stays quiet by design.
 */
export function toastApiError(
  error: unknown,
  fallback: string,
  message?: string | null,
): boolean {
  const text = resolveToastMessage(error, fallback, message);
  if (text === null) return false;
  toast.error(text);
  return true;
}

/**
 * The whole decision, without the toast — what `toastApiError` would say, or
 * `null` for "say nothing, the user has already been told".
 *
 * Split out because `sonner` needs a DOM and this runner has none: keeping
 * the rule testable matters more here than the one line it saves, since the
 * rule is what decides whether a user ever learns why something failed.
 */
export function resolveToastMessage(
  error: unknown,
  fallback: string,
  message?: string | null,
): string | null {
  if (wasErrorToasted(error)) return null;
  return message || getErrorMessage(error, fallback);
}

/**
 * Report a failed submit: the fields the server named go under their fields
 * (`apply` sets them and leads to the first); anything it could not place —
 * or a failure that is not a 422 at all — is one toast.
 */
export function reportFormError<K extends string>(
  error: unknown,
  map: Record<string, K>,
  apply: (fields: Partial<Record<K, string>>) => void,
  fallback: string,
): void {
  const read = readFieldErrors(error, map);
  if (read && Object.keys(read.fields).length > 0) {
    apply(read.fields);
    if (read.rest.length > 0) toast.error(read.rest[0]);
    return;
  }
  toastApiError(error, fallback);
}
